import { useEffect, useState } from 'react';
import './BeteckningComponent.css';

import Feature from 'ol/Feature';
import Geometry from 'ol/geom/Geometry';
import { KartstodModel, Koordinater } from '../model/KartstodModel';
import { highLightBeteckning, setInputFields } from '../utils/FsokUtils';

import { Delete as DeleteIcon, Description } from '@mui/icons-material';
import IconButton from '@mui/material/IconButton';
import ModalInfotext from '../ModalInfotext';
import BeteckningarLista from './BeteckningarLista';

import { Checkbox } from '@mui/material';
import { grey } from '@mui/material/colors';
import { usePersistedState } from '../../common/hooks/usePersistedState';
import { hashCode } from '../../common/miscFunctions';
import { fastigheterStorageKey } from '../../forteckningar/Forteckningar';
import * as Client from '../KartStodClient';
import KopKnapp from '../KopKnapp';
import { useDataContext } from '../contexts/DataContext';
import { useKartstodContext } from '../contexts/KartstodContext';
import { FeatureUtils } from '../utils/FeatureUtils';
import { Loggning } from '../utils/Loggning';
import { URLUtils } from '../utils/URLUtils';

interface BeteckningComponentProps {
  json: any;
  setJson: (js: any) => void;
  beteckningar: Feature<Geometry>[];

  setKlickadBeteckning: (beteckning: Feature<Geometry>[]) => void;
  setHovradBeteckning: (beteckning: Feature<Geometry>[]) => void;
  markeradeBeteckningar: any;
  setMarkeradeBeteckningar: (feature: any) => void;

  visaKopKnapp: boolean;
  setVisaKopknapp: (b: boolean) => void;
  isBeteckningarOpen: boolean;
  setIsBeteckningarOpen: (b: boolean) => void;
  zoomLevel: boolean;
  fetchBeteckningarUrl: string;
  koordinater: Koordinater;
  isPremium: boolean;
}

function createFastighet(fastighet: string): KartstodModel {
  return {
    id: hashCode(fastighet),
    fastighet,
  };
}

function BeteckningComponent(props: BeteckningComponentProps) {
  const setSkapadAF = usePersistedState<KartstodModel[]>(
    fastigheterStorageKey,
    []
  )[1];
  const [agarForteckningList, setAgarforteckningList] = useState<string[]>([]);

  const [markeradFranLista, setMarkeradFranLista] = useState(false);
  const [visaHjalpRutaAgarforteckning, setVisaHjalpRutaAgarforteckning] =
    useState(false);
  const [visaHjalpRutaTaBort, setVisaHjalpRutaTaBort] = useState(false);

  const [counterTotal, setCounterTotal] = useState(0);
  const [counterMarkerade, setCounterMarkerade] = useState(0);

  const { setVisaFelmeddelande, setFelmeddelande } = useKartstodContext();
  const {
    listaBeteckningsObjekt,
    setListaBeteckningsObjekt,
    setKlickFranKarta,
  } = useDataContext();

  useEffect(() => {
    loadBeteckningsListOnFirstRun();
  }, []);

  const loadBeteckningsListOnFirstRun = () => {
    const beteckningsLista = props.json.map((json: any) => ({
      json,
      markerad: false,
    }));
    setListaBeteckningsObjekt(beteckningsLista);
  };

  useEffect(() => {
    const counterMarkerade = listaBeteckningsObjekt
      .map((obj) => obj.markerad)
      .filter((value) => value).length;

    const counterTotal = listaBeteckningsObjekt.length;

    setCounterTotal(counterTotal);
    setCounterMarkerade(counterMarkerade);
  }, [listaBeteckningsObjekt, markeradFranLista]);

  useEffect(() => {
    const markeringar = listaBeteckningsObjekt.some((obj) => obj.markerad);

    markeringar ? setMarkeradFranLista(true) : setMarkeradFranLista(false);
  }, [listaBeteckningsObjekt]);

  const hamtaBeteckning = () => {
    if (props.zoomLevel) {
      setVisaFelmeddelande(true);
      setFelmeddelande('Ospecifik markering. Testa att zooma in ytterligare.');
    } else {
      setVisaFelmeddelande(false);

      const featureCollectionFromWMS = Client.fetchDataWMS(
        props.fetchBeteckningarUrl
      )
        .then((featureCollection) => {
          const featureArray =
            FeatureUtils.tvattaBortOtillatnaDetaljtyperIFeatureCollection(
              featureCollection
            );

          if (FeatureUtils.existObjektId(featureArray)) {
            if (
              FeatureUtils.isFeatureDuplicate(props.beteckningar, featureArray)
            ) {
              setVisaFelmeddelande(true);
              setFelmeddelande('Du har redan hämtat denna fastighet.');
            } else {
              setVisaFelmeddelande(false);
              props.setIsBeteckningarOpen(true);
              props.setVisaKopknapp(false);

              return featureArray;
            }
          } else {
            setVisaFelmeddelande(true);
            setFelmeddelande('Ingen fastighet funnen för denna markering.');
          }
        })
        .catch((e) => console.log('Kan ej hämta data från WMS.' + e));

      featureCollectionFromWMS
        .then((featureArray) => {
          const beteckning = FeatureUtils.hamtaForstaBeteckningen(featureArray);
          setInputFields(beteckning);
          highLightBeteckning();

          if (FeatureUtils.existsGAANLAGGNING(featureArray)) {
            const kommunnamn =
              FeatureUtils.getEventuelltKommunNanmnFranFeatureCollection(
                featureArray
              );
            FeatureUtils.addKommunnamnToGAAnlaggning(featureArray, kommunnamn);
          }

          const objektId = featureArray
            .filter(
              (feature) => feature.properties['detaljtyp'] === 'FASTIGHET'
            )
            .map((feature) => feature.properties['objekt_id']);

          //Om det är 1 st fastighet - kolla för eventuella skiften till fastigheten. Övriga detaljtyper läggs sedan på som features i samma json-array.
          if (objektId.length > 0) {
            const WFSUrl = URLUtils.skapaWFSUrl(objektId);

            Client.fetchDataWFS(WFSUrl)
              .then((featureCollection) => {
                const ovrigaFeatures = featureArray.filter(
                  (feature) => feature.properties['detaljtyp'] !== 'FASTIGHET'
                );
                const ovrigaFeaturesPlusFeatureCollectionsFranWFS = [
                  featureCollection,
                  ...ovrigaFeatures,
                ];
                props.setJson((jsondata) =>
                  [
                    ovrigaFeaturesPlusFeatureCollectionsFranWFS,
                    ...jsondata,
                  ].flat()
                );

                const nyaListObjekt =
                  ovrigaFeaturesPlusFeatureCollectionsFranWFS
                    .map((json: any) => ({ json, markerad: false }))
                    .flat();
                setListaBeteckningsObjekt([
                  ...nyaListObjekt,
                  ...listaBeteckningsObjekt,
                ]);
              })
              .catch((e) => console.log('Kan ej hämta data från WFS.' + e));
          } else {
            const ovrigaFeatures = featureArray.filter(
              (fet) => fet.properties['detaljtyp'] !== 'FASTIGHET'
            );
            props.setJson((jsondata) => [ovrigaFeatures, ...jsondata].flat());

            const nyaListObjekt = ovrigaFeatures.map((feature: any) => ({
              json: feature,
              markerad: false,
            }));
            setListaBeteckningsObjekt([
              ...nyaListObjekt,
              ...listaBeteckningsObjekt,
            ]);
          }

          if (!props.isPremium) {
            Loggning.loggaTransaktion(
              props.koordinater.nKoordinat,
              props.koordinater.eKoordinat
            );
          }
        })
        .catch((e) => 'Oväntat fel i underliggande system.' + e);
    }
  };

  const taBortBeteckning = (bet: string) => {
    const kvarvarandeJson = props.json.filter((js: any) =>
      FeatureUtils.getAllaUtomAngivenBeteckning(js, bet)
    );

    const kvarvarandeAgarforteckningar = agarForteckningList.filter(
      (b: string) => b !== bet
    );

    const kvarvarandeMarkeradeBeteckningar = listaBeteckningsObjekt
      .filter((obj) => FeatureUtils.getAllaUtomAngivenBeteckning(obj.json, bet))
      .filter((obj) => obj.markerad)
      .map((obj) => obj.json);

    const kvarvarandeListaBeteckningsObjekt = listaBeteckningsObjekt.filter(
      (obj: any) => FeatureUtils.getAllaUtomAngivenBeteckning(obj.json, bet)
    );

    props.setJson(kvarvarandeJson.flat());
    setAgarforteckningList(kvarvarandeAgarforteckningar);
    props.setMarkeradeBeteckningar(kvarvarandeMarkeradeBeteckningar);
    setListaBeteckningsObjekt(kvarvarandeListaBeteckningsObjekt);
    props.setHovradBeteckning([]);
  };

  const hovraOverBeteckning = (bet: string) => {
    const beteckning = props.json.filter((js: any) =>
      FeatureUtils.getAngivenBeteckning(js, bet)
    );
    props.setHovradBeteckning(beteckning);
  };

  const klickaPaBeteckning = (bet: string) => {
    const beteckning = props.json.filter((js: any) =>
      FeatureUtils.getAngivenBeteckning(js, bet)
    );
    props.setKlickadBeteckning(beteckning);
  };

  const markeraBeteckning = (bet) => {
    const nyListaBeteckningsObjekt = listaBeteckningsObjekt.map((objLista) =>
      FeatureUtils.togglaEnMarkeringFranObjektLista(objLista, bet)
    );

    setListaBeteckningsObjekt(nyListaBeteckningsObjekt);
    setKlickFranKarta('');

    const valdBeteckning = nyListaBeteckningsObjekt.filter((obj: any) =>
      FeatureUtils.getAngivenBeteckning(obj.json, bet)
    );

    if (valdBeteckning[0].markerad) {
      props.setMarkeradeBeteckningar([
        ...props.markeradeBeteckningar,
        valdBeteckning[0].json,
      ]);
      setAgarforteckningList([bet, ...agarForteckningList]);
    } else {
      const beteckningar = agarForteckningList.filter((b: string) => b !== bet);

      setAgarforteckningList(beteckningar);

      const kvarvarandeMarkeradeBeteckningar = nyListaBeteckningsObjekt
        .filter((obj) => obj.markerad)
        .map((obj) => obj.json);

      props.setMarkeradeBeteckningar(kvarvarandeMarkeradeBeteckningar);
    }
  };

  /*
   *
   * Verktygspanel
   *
   */

  const togglaAllaBeteckningar = () => {
    const isAllaBeteckningarMarkerade = listaBeteckningsObjekt.every(
      (obj) => obj.markerad
    );

    if (isAllaBeteckningarMarkerade) {
      const listObjAllFalse = listaBeteckningsObjekt.map((obj) => {
        return { ...obj, markerad: false };
      });

      setListaBeteckningsObjekt(listObjAllFalse);
      setAgarforteckningList([]);
      setMarkeradFranLista(false);
      props.setMarkeradeBeteckningar([]);
    } else {
      const listObjAllTrue = listaBeteckningsObjekt.map((obj) => {
        return { ...obj, markerad: true };
      });

      setListaBeteckningsObjekt(listObjAllTrue);

      const beteckningarTillAgarForteckningar = listaBeteckningsObjekt
        .map((obj: any) => FeatureUtils.getAllaBeteckningarFranObjektlista(obj))
        .flat();

      setAgarforteckningList(beteckningarTillAgarForteckningar);
      setMarkeradFranLista(true);
      props.setMarkeradeBeteckningar([props.json].flat());
    }
  };

  const skapaAgarForteckning = () => {
    const tvattadeBeteckningar = agarForteckningList.map(
      (bet: string) => bet.split('>')[0]
    );

    const unikaBeteckningar = tvattadeBeteckningar
      .filter((bet, pos) => tvattadeBeteckningar.indexOf(bet) === pos)
      .map((unikBet) => createFastighet(unikBet));

    setSkapadAF(unikaBeteckningar);
    setVisaHjalpRutaAgarforteckning(false);
  };

  const taBortFleraMarkeradeBeteckningar = () => {
    const kvarvarandeJson = listaBeteckningsObjekt
      .filter((obj) => !obj.markerad)
      .map((obj) => obj.json);

    const kvarvarandeListaBeteckningsObjekt = listaBeteckningsObjekt.filter(
      (obj) => !obj.markerad
    );

    setListaBeteckningsObjekt(kvarvarandeListaBeteckningsObjekt);
    props.setJson(kvarvarandeJson);
    props.setMarkeradeBeteckningar([]);
    setAgarforteckningList([]);
    setVisaHjalpRutaTaBort(false);
  };

  const toggleVisaMeny = () => {
    props.setIsBeteckningarOpen(!props.isBeteckningarOpen);
  };

  const jumpToAF = () => {
    setTimeout(() => {
      if (typeof window !== 'undefined') {
        window.location.href = '/fsok/Forteckningar.seam';
      }
    }, 500);
  };

  return (
    <div
      className={`beteckningar-container ${
        props.isBeteckningarOpen ? 'open-bet' : 'closed-bet'
      }`}
    >
      <div
        className={`widget ${props.isBeteckningarOpen ? 'shadow' : 'unshadow'}`}
        onClick={toggleVisaMeny}
      >
        <i
          className={`arrow-ks ${
            props.isBeteckningarOpen ? 'right-ks' : 'left-ks'
          }`}
        />
      </div>
      <div
        className={`beteckningar-list ${
          props.isBeteckningarOpen ? 'shadow' : 'unshadow'
        }`}
      >
        <div className="beteckningar-header">Fastighetsbeteckningar</div>
        <div className="toolbox-container">
          <Checkbox
            checked={markeradFranLista}
            onClick={() => {
              togglaAllaBeteckningar();
            }}
          />
          {markeradFranLista ? (
            <div className="edit-toolbox">
              <IconButton
                aria-label="delete"
                sx={{
                  padding: '9px',
                  marginLeft: 'auto',
                  color: grey[800],
                }}
                onMouseEnter={() => setVisaHjalpRutaAgarforteckning(true)}
                onMouseLeave={() => setVisaHjalpRutaAgarforteckning(false)}
                onClick={() => {
                  skapaAgarForteckning();
                  jumpToAF();
                }}
              >
                <Description />
              </IconButton>
              <IconButton
                aria-label="delete"
                sx={{
                  padding: '9px',
                  marginLeft: 'auto',
                  color: grey[800],
                }}
                onMouseEnter={() => setVisaHjalpRutaTaBort(true)}
                onMouseLeave={() => setVisaHjalpRutaTaBort(false)}
                onClick={() => taBortFleraMarkeradeBeteckningar()}
              >
                <DeleteIcon />
              </IconButton>
            </div>
          ) : null}
          <div className="toolbox-counter">
            {counterMarkerade} av {counterTotal}
          </div>
          {visaHjalpRutaAgarforteckning ? (
            <ModalInfotext
              meddelande={'Lägg till ägarförteckning'}
              style={{
                top: '50px',
                left: '20px',
                width: '134px',
                height: '12px',
              }}
            />
          ) : null}
          {visaHjalpRutaTaBort ? (
            <ModalInfotext
              meddelande={'Ta bort'}
              style={{
                top: '50px',
                left: '84px',
                width: '40px',
                height: '12px',
              }}
            />
          ) : null}
        </div>
        <BeteckningarLista
          klickaPaBeteckning={(beteckning) => klickaPaBeteckning(beteckning)}
          hovraOverBeteckning={(beteckning) => hovraOverBeteckning(beteckning)}
          markeraBeteckning={(beteckning) => markeraBeteckning(beteckning)}
          taBortBeteckning={(beteckning) => taBortBeteckning(beteckning)}
        />
      </div>
      <KopKnapp
        visaKopKnappGeometrier={props.visaKopKnapp}
        setVisaKopKnapp={(visaKopKnapp) => props.setVisaKopknapp(visaKopKnapp)}
        hamtaBeteckning={hamtaBeteckning}
      />
    </div>
  );
}

export default BeteckningComponent;
